import { create } from 'zustand';
import type {
  ReleaseListItemDto,
  ReleaseDto,
  CreateReleaseRequest,
  UpdateReleaseRequest,
} from '../types';
import { releaseNoteService } from '../services';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

interface ReleaseNoteState {
  // Public list (published only)
  publishedReleases: ReleaseDto[];
  isLoadingPublished: boolean;
  publishedError: string | null;

  // Admin list (all)
  adminReleases: ReleaseListItemDto[];
  isLoadingAdmin: boolean;
  adminError: string | null;

  // Selected release detail
  selectedRelease: ReleaseDto | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Submit state
  isSubmitting: boolean;

  // Actions
  fetchPublishedReleases: () => Promise<void>;
  fetchPublishedReleaseDetails: () => Promise<void>;
  fetchAdminReleases: () => Promise<void>;
  fetchReleaseDetail: (id: number) => Promise<void>;
  createRelease: (request: CreateReleaseRequest) => Promise<boolean>;
  updateRelease: (id: number, request: UpdateReleaseRequest) => Promise<boolean>;
  deleteRelease: (id: number) => Promise<boolean>;
  clearSelectedRelease: () => void;
}

export const useReleaseNoteStore = create<ReleaseNoteState>()((set, get) => ({
  publishedReleases: [],
  isLoadingPublished: false,
  publishedError: null,

  adminReleases: [],
  isLoadingAdmin: false,
  adminError: null,

  selectedRelease: null,
  isLoadingDetail: false,
  detailError: null,

  isSubmitting: false,

  fetchPublishedReleases: async () => {
    set({ isLoadingPublished: true, publishedError: null });
    try {
      const releases = await releaseNoteService.getPublished();
      set({ publishedReleases: [], isLoadingPublished: false });
      // Now fetch full details for each published release
      const detailed = await Promise.all(
        releases.map((r) => releaseNoteService.getById(r.id)),
      );
      set({ publishedReleases: detailed });
    } catch (error) {
      set({
        publishedError: getApiErrorMessage(error, 'Failed to fetch releases'),
        isLoadingPublished: false,
      });
    }
  },

  fetchPublishedReleaseDetails: async () => {
    // Alternative: fetch list then details in one shot
    set({ isLoadingPublished: true, publishedError: null });
    try {
      const releases = await releaseNoteService.getPublished();
      const detailed = await Promise.all(
        releases.map((r) => releaseNoteService.getById(r.id)),
      );
      set({ publishedReleases: detailed, isLoadingPublished: false });
    } catch (error) {
      set({
        publishedError: getApiErrorMessage(error, 'Failed to fetch releases'),
        isLoadingPublished: false,
      });
    }
  },

  fetchAdminReleases: async () => {
    set({ isLoadingAdmin: true, adminError: null });
    try {
      const releases = await releaseNoteService.getAllAdmin();
      set({ adminReleases: releases, isLoadingAdmin: false });
    } catch (error) {
      set({
        adminError: getApiErrorMessage(error, 'Failed to fetch releases'),
        isLoadingAdmin: false,
      });
    }
  },

  fetchReleaseDetail: async (id: number) => {
    set({ isLoadingDetail: true, detailError: null });
    try {
      const release = await releaseNoteService.getByIdAdmin(id);
      set({ selectedRelease: release, isLoadingDetail: false });
    } catch (error) {
      set({
        detailError: getApiErrorMessage(error, 'Failed to fetch release'),
        isLoadingDetail: false,
      });
    }
  },

  createRelease: async (request: CreateReleaseRequest) => {
    set({ isSubmitting: true });
    try {
      await releaseNoteService.create(request);
      set({ isSubmitting: false });
      get().fetchAdminReleases();
      return true;
    } catch (error) {
      set({ isSubmitting: false });
      throw error;
    }
  },

  updateRelease: async (id: number, request: UpdateReleaseRequest) => {
    set({ isSubmitting: true });
    try {
      await releaseNoteService.update(id, request);
      set({ isSubmitting: false });
      get().fetchAdminReleases();
      return true;
    } catch (error) {
      set({ isSubmitting: false });
      throw error;
    }
  },

  deleteRelease: async (id: number) => {
    set({ isSubmitting: true });
    try {
      await releaseNoteService.delete(id);
      set({ isSubmitting: false });
      get().fetchAdminReleases();
      return true;
    } catch (error) {
      set({ isSubmitting: false });
      throw error;
    }
  },

  clearSelectedRelease: () => set({ selectedRelease: null }),
}));
